import {
  Component,
  EventEmitter,
  Output,
  ViewChild,
  ElementRef,
  Input,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormControl,
  Validators,
} from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { ReserveTableService } from '../services/reserve-table.service';
import { PaymentService } from '../services/payment.service';
import { AlertService } from '../services/alert.service';
import { nameValidator } from '../../validators/name.validator';

interface DateOption {
  day: string;
  date: number;
  month: string;
  value: string;
}

interface TimeSlot {
  time: string;
  isBooked: boolean;
}

interface Table {
  id: string;
  name: string;
  description: string;
  available: boolean;
}

interface MealSlots {
  openingTime: string;
  closingTime: string;
  slots: {
    startTime: string;
    availableSeats: number;
    isAvailable: boolean;
    occupiedTables: number;
    totalTables: number;
  }[];
}

interface SlotsApiResponse {
  restaurantId: string;
  restaurantName: string;
  reservationDate: string;
  pricePerSeat: number;
  slotGap: number;
  slotHoldDuration: number;
  totalCapacity: number;
  timezone: string;
  breakfast: MealSlots;
  lunch: MealSlots;
  dinner: MealSlots;
}

@Component({
  selector: 'app-reserve-table-popup',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, HttpClientModule],
  templateUrl: './reserve-table-popup.component.html',
  styleUrls: ['./reserve-table-popup.component.scss'],
})
export class ReserveTablePopupComponent implements OnInit {
  @Output() closePopup = new EventEmitter<void>();
  @ViewChild('dateCardsContainer') dateCardsContainer!: ElementRef;
  @Input() restaurantId!: string;

  @ViewChild('nameField') nameField!: ElementRef;
  @ViewChild('mobileField') mobileField!: ElementRef;
  @ViewChild('emailField') emailField!: ElementRef;


  personsControl = new FormControl(2);
  dateControl = new FormControl('');
  timeSlotControl = new FormControl<string | null>(null);
  tableControl = new FormControl('');

  nameControl = new FormControl('', [
    Validators.required,
    Validators.minLength(3),
    nameValidator
  ]);


  mobileControl = new FormControl('', [
    Validators.required,
    Validators.pattern(/^[6-9]\d{9}$/)
  ]);

  emailControl = new FormControl('', [
  Validators.required,
  Validators.email
]);



  loadingTimeSlots = false;

  availableDates: DateOption[] = [];
  allTimeSlots: TimeSlot[] = [];
  tables: Table[] = [];

  restaurantData: any;
  minPricePerPerson = 0;
  loading = false;

  paymentLoading = false;

  slotsResponse: SlotsApiResponse | null = null;
  selectedMeal: 'breakfast' | 'lunch' | 'dinner' = 'lunch';

  constructor(
    private reserveService: ReserveTableService,
    private paymentService: PaymentService,
    private alertService: AlertService
  ) { }

  ngOnInit(): void {
    this.generateDates();
    if (this.restaurantId) {
      this.fetchDiningSettings();
    }
  }

  allowOnlyNumbers(event: KeyboardEvent): void {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }

  allowOnlyLetters(event: KeyboardEvent): void {
    const char = event.key;
    if (!/^[a-zA-Z]$/.test(char)) {
      event.preventDefault();
    }
  }

  fetchDiningSettings(): void {
    this.loading = true;
    this.reserveService.getDiningSettings(this.restaurantId).subscribe({
      next: (res: any) => {
        this.restaurantData = res;
        this.minPricePerPerson =
          res?.reservationSettings?.[0]?.pricePerSeat || 0;

        this.tables = (res.diningTables || []).map((t: any) => ({
          id: t.tableCode,
          name: `Table ${t.tableCode}`,
          description: `Capacity: ${t.capacity}`,
          available: t.isAvailable,
        }));

        this.loading = false;
      },
      error: (err) => {
        console.error('Error fetching dining settings:', err);
        this.loading = false;
      },
    });
  }

  fetchTimeSlots(): void {
    const date = this.dateControl.value;
    if (!date || !this.restaurantId || !this.restaurantData) return;

    const today = new Date();
    const selectedDate = new Date(date);
    const isToday = selectedDate.toDateString() === today.toDateString();

    const startTime = isToday
      ? this.formatCurrentTime(today)
      : this.restaurantData.openingTime || '12:00 AM';

    this.loadingTimeSlots = true;
    this.timeSlotControl.reset();

    this.reserveService
      .getAvailableSlots(this.restaurantId, date, startTime)
      .subscribe({
        next: (res: SlotsApiResponse) => {
          this.slotsResponse = res;
          this.setDefaultMealForNow();
          this.buildTimeSlots();
          this.loadingTimeSlots = false;
        },
        error: (err) => {
          console.error('Error fetching time slots:', err);
          this.loadingTimeSlots = false;
          this.allTimeSlots = [];

          const msg =
            err?.error?.message || 'Failed to fetch time slots. Please try again later.';
          this.alertService.toast({
            icon: 'error',
            title: msg,
          });
        },
      });
  }


  private setDefaultMealForNow(): void {
    if (!this.slotsResponse) return;

    const now = new Date();
    const hour = now.getHours();

    const breakfast = this.getMealData('breakfast');
    const lunch = this.getMealData('lunch');
    const dinner = this.getMealData('dinner');

    if (hour < 11 && breakfast?.slots?.length) {
      this.selectedMeal = 'breakfast';
    } else if (hour < 16 && lunch?.slots?.length) {
      this.selectedMeal = 'lunch';
    } else if (dinner?.slots?.length) {
      this.selectedMeal = 'dinner';
    } else if (lunch?.slots?.length) {
      this.selectedMeal = 'lunch';
    } else if (breakfast?.slots?.length) {
      this.selectedMeal = 'breakfast';
    } else {
      this.selectedMeal = 'dinner';
    }
  }

  private getMealData(
    meal: 'breakfast' | 'lunch' | 'dinner'
  ): MealSlots | null {
    if (!this.slotsResponse) return null;
    if (meal === 'breakfast') return this.slotsResponse.breakfast;
    if (meal === 'lunch') return this.slotsResponse.lunch;
    return this.slotsResponse.dinner;
  }

  private buildTimeSlots(): void {
    const mealData = this.getMealData(this.selectedMeal);
    if (!mealData || !mealData.slots) {
      this.allTimeSlots = [];
      return;
    }

    this.allTimeSlots = mealData.slots.map((slot) => ({
      time: slot.startTime,
      isBooked: !slot.isAvailable || slot.availableSeats === 0,
    }));
  }

  changeMeal(meal: 'breakfast' | 'lunch' | 'dinner'): void {
    if (this.selectedMeal === meal) return;

    this.selectedMeal = meal;
    this.timeSlotControl.reset();

    this.buildTimeSlots();
  }


  private formatCurrentTime(date: Date): string {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  }

  generateDates(): void {
    const today = new Date();
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    this.availableDates = Array.from({ length: 14 }).map((_, i) => {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      return {
        day: days[date.getDay()],
        date: date.getDate(),
        month: months[date.getMonth()],
        value: date.toISOString().split('T')[0],
      };
    });
  }

  selectDate(date: string): void {
    this.dateControl.setValue(date);
    this.timeSlotControl.reset();
    this.allTimeSlots = [];

    this.fetchTimeSlots();
  }


  get filteredTimeSlots(): TimeSlot[] {
    return this.allTimeSlots;
  }

  get totalPrice(): number {
    return (Number(this.personsControl.value) || 0) * this.minPricePerPerson;
  }

  selectTimeSlot(slot: TimeSlot): void {
    if (slot.isBooked) return;
    this.timeSlotControl.setValue(slot.time);
  }

  selectTable(tableId: string): void {
    this.tableControl.setValue(tableId);
  }

  incrementGuests(): void {
    const current = Number(this.personsControl.value) || 0;
    if (current < 12) this.personsControl.setValue(current + 1);
  }

  decrementGuests(): void {
    const current = Number(this.personsControl.value) || 0;
    if (current > 1) this.personsControl.setValue(current - 1);
  }

  scrollDates(direction: 'left' | 'right'): void {
    const container = this.dateCardsContainer?.nativeElement;
    if (container) {
      const scrollAmount = direction === 'left' ? -200 : 200;
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  }

  confirm(): void {
    this.nameControl.markAsTouched();
    this.mobileControl.markAsTouched();
    this.emailControl.markAsTouched();
    this.dateControl.markAsTouched();
    this.timeSlotControl.markAsTouched();

    if (this.nameControl.invalid) {
      this.scrollToError(this.nameField);
      return;
    }

    if (this.mobileControl.invalid) {
      this.scrollToError(this.mobileField);
      return;
    }

    if (this.emailControl.invalid) {
    this.scrollToError(this.emailField);
    return;
  }

    if (!this.dateControl.value || !this.timeSlotControl.value) {
      this.alertService.toast({
        icon: 'warning',
        title: 'Please select date and time slot.',
      });
      return;
    }

    const totalAmount = this.totalPrice;

    const reservationData = {
      reservationDate: this.dateControl.value,
      startTime: this.timeSlotControl.value,
      numberOfGuests: this.personsControl.value,
      customerName: this.nameControl.value?.trim(),
      customerContact: this.mobileControl.value,
      customerEmail: this.emailControl.value,
      specialRequests: '',
      totalAmount,
    };

    this.loading = true;

    this.reserveService
      .makeReservation(this.restaurantId, reservationData)
      .subscribe({
        next: async (res: any) => {
          this.loading = false;
          const reservationId = res?.reservation?.id;

          if (!reservationId) {
            this.alertService.toast({
              icon: 'error',
              title:
                'Failed to confirm reservation. Please try again later.',
            });
            return;
          }

          if (!totalAmount || totalAmount <= 0) {
            this.alertService.toast({
              icon: 'success',
              title: 'Your reservation is confirmed.',
            });
            this.close();
            return;
          }

          try {
            this.paymentLoading = true;
            const paymentResult = await this.paymentService.startPayment(
              reservationId,
              totalAmount,
              this.nameControl.value ?? '',
              this.mobileControl.value ?? '',
              this.emailControl.value ?? '',
            );
            this.paymentLoading = false;

            if (paymentResult?.success) {
              this.alertService.toast({
                icon: 'success',
                title:
                  'Thank you! Your payment was successful and your reservation is confirmed.',
              });
              this.close();
            } else {
              this.alertService.toast({
                icon: 'error',
                title:
                  'Payment failed or was cancelled. Please try again.',
              });
            }
          } catch (err) {
            this.paymentLoading = false;
            console.error('Payment error:', err);
            this.alertService.toast({
              icon: 'error',
              title:
                'Something went wrong during payment. Please try again.',
            });
          }
        },
        error: (err: any) => {
          this.loading = false;
          console.error('Error creating reservation:', err);

          const errMsg =
            err?.error?.message ||
            err?.message ||
            'Failed to create reservation. Please try again later.';

          this.alertService.toast({
            icon: 'error',
            title: errMsg,
          });
        },

      });
  }

  close(): void {
    this.closePopup.emit();
  }

  closePopupOverlay(event?: MouseEvent): void {
    if (event && event.target !== event.currentTarget) return;
    this.close();
  }

  handleNameKeyDown(event: KeyboardEvent): void {
    const value = this.nameControl.value || '';
    const key = event.key;

    if (
      key === 'Backspace' ||
      key === 'Delete' ||
      key === 'ArrowLeft' ||
      key === 'ArrowRight' ||
      key === 'Tab'
    ) {
      return;
    }

    if (key === ' ' && value.length === 0) {
      event.preventDefault();
      return;
    }

    if (key === ' ' && value.endsWith(' ')) {
      event.preventDefault();
      return;
    }

    if (!/^[a-zA-Z ]$/.test(key)) {
      event.preventDefault();
    }
  }

  sanitizeName(): void {
    let value = this.nameControl.value || '';
    value = value.replace(/^\s+/, '');
    value = value.replace(/\s{2,}/g, ' ');

    this.nameControl.setValue(value, { emitEvent: false });
  }

  scrollToError(element: ElementRef): void {
    setTimeout(() => {
      element.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }, 100);
  }

}
